import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Sun, Moon, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import { useThemeStore } from '@/stores/theme'
import { useSubscription } from '@/hooks/useSubscription'
import { useMyOrganization } from '@/hooks/useMyOrganization'
import { NotificationBell } from '@/components/notifications/notification-bell'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/loader'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { LanguageMenuItems } from '@/components/ui/language-menu-items'
import { HelpLauncher } from '@/components/help/help-launcher'
import { FeedbackLauncher } from '@/components/feedback/feedback-launcher'
import { planKeyForTier } from '@/lib/pricing'

/**
 * The three SYSTEM controls — theme, notifications, account — in one component
 * with two presentations.
 *
 * Round 18, frame `18d`: «Tema, klokke, avatar ned i skinnen … de tre er
 * systemkontroller, ikke sideinnhold. Under en skillelinje, adskilt fra
 * navigasjonen, 44 px treffflate – større enn dagens 32.»
 *
 * ⚠️ Why this is ONE component and not two copies. `sidebar.tsx` is
 * `hidden lg:flex`, so the rail does not exist below 1024px, and
 * `bottom-nav.tsx` has no bell, no theme toggle and — critically — no sign-out.
 * Moving these into the rail without a second home below `lg` removes sign-out
 * from every phone user, and `npm run build` and `npm run lint` both stay
 * green. The `rail` variant renders at `lg+`, the `bar` variant below it, and
 * `Topbar` hides its own cluster at `lg+` so exactly one instance exists at
 * every viewport. Gate G8 (`design-review/controls-gate.mjs`) is what proves it.
 *
 * Everything below the presentation layer — the theme-toggle semantics, the
 * bell, the menu contents — is shared, so the two variants cannot drift.
 */
interface AccountControlsProps {
  /** `bar` = horizontal icon row in the top bar. `rail` = sidebar footer. */
  variant: 'bar' | 'rail'
  /** Rail only: the sidebar is collapsed to the 68px icon rail. */
  collapsed?: boolean
  className?: string
}

export function AccountControls({ variant, collapsed = false, className }: AccountControlsProps) {
  const { t } = useTranslation()
  const { profile, signOut } = useAuth()
  const { setTheme, effectiveMode } = useTheme()
  const { isPro, tier } = useSubscription()
  // The plan's own name, as /settings/billing says it: «Pro» was the only word this badge knew,
  // so an Enterprise account read Pro here and Enterprise there (30.10.2026).
  const planLabel = isPro ? t(`pricing.plans.${planKeyForTier(tier)}.name`) : t('profile.free')
  const { memberships, active: activeOrg } = useMyOrganization()
  const navigate = useNavigate()
  const location = useLocation()

  /**
   * Switching tenant reloads, and that is the honest option rather than the
   * lazy one.
   *
   * The active organisation decides the effective tier, which senders are
   * selectable, who may send a broadcast, which brand settings apply and which
   * events are in scope. Those are read by a dozen hooks, several of them
   * behind `lib/query-cache.ts`. Refetching «the org-scoped ones» means
   * maintaining a list of what is org-scoped, and that list is wrong the first
   * time somebody adds a hook and does not know it exists.
   *
   * A reload cannot go stale. `useMyOrganization` already anticipated this —
   * «Caller (Topbar switcher) typically triggers a window-level refresh to
   * invalidate org-scoped query caches» — and it is what Linear and Clerk do
   * on a workspace switch, because switching identity is a deliberate act and
   * a full repaint is what the user expects from it.
   */
  const switchOrg = (orgId: string) => {
    if (orgId === activeOrg?.org.id) return
    // Navigation must pass the current editor's guard BEFORE changing tenant.
    // The transition route unmounts that editor, switches, then reloads caches.
    navigate(`/organization/switch/${encodeURIComponent(orgId)}`, {
      state: { returnTo: `${location.pathname}${location.search}${location.hash}` },
    })
  }

  // Flip away from what is ON THE SCREEN, not from what is in the store.
  //
  // Those differ in two states, and the button was wrong in both: under
  // `system` the stored value is neither side, and under an event lock the
  // document shows the event's mode while `resolvedTheme` still reports the
  // viewer's. Reading `effectiveMode` collapses all three cases to one rule —
  // press it and you get the other one — which is the only contract a person
  // can hold about a control with two states.
  //
  // Read at CALL time, not from the render closure. Two presses inside one
  // React batch — a double-click, and people double-click a control that
  // looked like it did nothing — both saw the same rendered `effectiveMode`,
  // so the second computed the same target as the first and the theme did not
  // move. Measured 11.08.2026: two synchronous clicks from `dark` produced
  // `light` and then `light` again. `getState()` is always current.
  const toggleTheme = () =>
    setTheme(useThemeStore.getState().effectiveMode === 'dark' ? 'light' : 'dark')

  const isRail = variant === 'rail'
  const railRow = isRail && !collapsed

  // Expanded, the rail draws labelled rows — `18a` shows «Lyst tema» and the
  // account row as full-width entries, matching the nav items above them. The
  // visible text and the accessible name are the SAME string on purpose: a
  // mode-dependent label («Lyst tema» / «Mørkt tema») would either break WCAG
  // 2.5.3 against a fixed aria-label, or rename the control on every toggle —
  // and renaming reads to the G8 ratchet as a control that disappeared. The
  // sun/moon icon carries the direction; the label says what the button does.
  const themeToggle = (
    <Button
      variant="quiet"
      size={railRow ? 'row' : 'icon'}
      onClick={toggleTheme}
      aria-label={t('common.toggleTheme')}
    >
      {effectiveMode === 'dark' ? (
        <Sun className="size-5" aria-hidden="true" />
      ) : (
        <Moon className="size-5" aria-hidden="true" />
      )}
      {railRow && <span className="truncate text-label">{t('common.toggleTheme')}</span>}
    </Button>
  )

  // The bell exists where no named notification destination is already
  // visible — the rule, not the breakpoint, is what decides.
  //
  // `18a`: «med en navngitt sidemeny er «Varsler» allerede et menypunkt med
  // teller, så barens klokke var det fjerde stedet samme tall sto. Bare tema og
  // avatar trengte et nytt hjem.» At `lg+` the rail lists «Varsler» with its own
  // count two rows up, so a bell beside it is the same number twice and a second
  // visible control with the same accessible name.
  //
  // Below `lg` the condition does not hold: `bottom-nav.tsx` puts «Varsler»
  // inside the "Mer" sheet, so nothing surfaces notifications until you tap
  // through. There the bell stays.
  //
  // Nothing is lost by dropping it: `/notifications` is a strict superset of the
  // popover — mark-all-read, snooze, mute, archive, the settings gear, plus
  // filters, bulk selection and date grouping the popover never had.
  const bell = isRail ? null : <NotificationBell />

  const accountMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'rounded-full cursor-pointer focus:outline-none focus-visible:focus-ring',
            isRail
              ? 'flex min-w-0 items-center rounded-[var(--radius-lg)] transition-colors hover:bg-[var(--surface-muted)]'
              /* The BAR variant is `lg:hidden` — mobile-only chrome, where
                 husregel 7 has no 24–43px allowance at all: below lg the floor
                 is a flat 44. Measured on every route: theme 32px, bell 32px,
                 avatar 39px. The rail was already correct; the bar was the
                 half of the same component nobody re-measured. */
              : 'ml-1 flex size-11 shrink-0 items-center justify-center',
            // Collapsed: a 44px icon target, the same as the nav items above.
            // Expanded: a full-width row lining up with them.
            isRail && (railRow ? 'h-11 w-full gap-3 px-3' : 'size-11 justify-center')
          )}
          aria-label={t('nav.profile')}
        >
          {profile ? (
            <Avatar name={profile.full_name ?? ''} src={profile.avatar_url} size="sm" />
          ) : (
            <Skeleton shape="round" className="size-8" />
          )}
          {railRow && (
            <span className="min-w-0 flex-1 text-left">
              {profile ? (
                <span className="block truncate text-label text-[var(--text-primary)]">
                  {profile.full_name}
                </span>
              ) : (
                <Skeleton className="h-3.5 w-20" />
              )}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={isRail ? 'start' : 'end'} side={isRail ? 'top' : 'bottom'} className="min-w-[220px]">
        <div className="px-2 py-1.5">
          {profile ? (
            <>
              <p className="truncate text-label text-[var(--text-primary)]">{profile.full_name}</p>
              <p className="truncate text-small text-[var(--text-muted)]">{profile.email}</p>
            </>
          ) : (
            <div className="space-y-1.5 py-0.5">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          )}
        </div>
        {/* ── Which customer am I working as? ──────────────────────────────
            The active tenant decides the effective tier, which sender IDs are
            selectable, who may send a broadcast at all, and which brand
            applies. Until now NOTHING in the shell named it: the only place it
            appeared anywhere was one sentence inside the Avsendere tab
            («Registreres hos operatøren for Compass Group»), so the answer to
            «whose data am I looking at» lived inside a feature instead of in
            the chrome. `useMyOrganization` has exported `setActive` the whole
            time and no component had ever called it.

            It belongs in THIS menu and not at the top of the rail: 17d's rule
            here is «de raske valgene», the things you change in the middle of
            doing something else, which is why language and the plan badge are
            already here. The rail's own rule (18d) puts system controls at its
            FOOT under a divider, so a workspace switcher at its head would be a
            third place identity is expressed rather than the first.

            «Personlig konto» is deliberately NOT offered. `useMyOrganization`
            accepts an explicit pointer only while it names a current
            membership, otherwise it falls back to the deterministic oldest
            membership. Writing NULL therefore snaps back to that membership
            on the next read. An entry that cannot hold its own
            state is worse than one that is absent, and making it hold would
            mean changing what «no active org» means for every other reader. */}
        {memberships.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('org.label')}</DropdownMenuLabel>
            {memberships.map((m) => {
              const isActive = m.org.id === activeOrg?.org.id
              return (
                <DropdownMenuItem
                  key={m.org.id}
                  onClick={() => void switchOrg(m.org.id)}
                  className="justify-between gap-3"
                >
                  {/* The tick is aria-hidden, so the state it carries is
                      repeated in text for a screen reader — the same pattern
                      the plan row below uses for its badge. */}
                  <span className="truncate">
                    {m.org.name}
                    {isActive && <span className="sr-only"> – {t('org.active')}</span>}
                  </span>
                  {isActive && <Check className="size-4 shrink-0" aria-hidden="true" />}
                </DropdownMenuItem>
              )
            })}
          </>
        )}
        <DropdownMenuSeparator />
        {/* Round 17d, «Avatarmenyen, før og etter». Before: «Profil» and
            «Kalender» — the second using t('nav.calendar'), the SAME key as the
            sidebar's calendar link, pointing somewhere else entirely. Two
            destinations, one label, in one shell. After: one entry at one depth.
            Everything that was behind those two now lives inside /settings.

            What stays here is only what 17d calls «de raske valgene» — the
            things you change in the middle of doing something else. Language,
            because it is switched from anywhere; the plan, because it is a
            status as much as a destination; and sign out. Theme is the third,
            and it is already one row away in the rail (18d). */}
        {/* «Innstillinger» is NOT here, and its absence is the fix.
            It was reachable twice at once in BOTH breakpoints, measured:
              · lg+   this menu opens directly ABOVE the rail's own
                      «Innstillinger» row — two entries, one destination,
                      forty pixels apart;
              · <lg   the same menu sits in the topbar while BottomNav's «Mer»
                      sheet carries `/settings` (bottom-nav.tsx:162).
            17d's rule for this menu is «de raske valgene» — the things you
            change in the middle of doing something else. A settings PAGE is a
            destination, not a quick choice, and navigation already owns it at
            every width. Abonnement stays because it is a STATUS as much as a
            destination: the plan is the thing you glance at here. */}
        <DropdownMenuItem asChild>
          <Link to="/settings/billing" className="justify-between gap-3">
            {/* sr-only copy carries the separator; see settings-nav.tsx. */}
            <span>
              {t('settings.billing')}
              <span className="sr-only"> – {planLabel}</span>
            </span>
            <Badge variant="neutral" size="sm" aria-hidden="true">
              {planLabel}
            </Badge>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('language.label')}</DropdownMenuLabel>
        <LanguageMenuItems />
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onClick={() => signOut()}>
          {t('auth.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  if (!isRail) {
    return (
      <div className={cn('flex shrink-0 items-center gap-1', className)}>
        <HelpLauncher />
        {/* Only for the people EventRipple switched on (03.10.2026); null for everyone else. */}
        <FeedbackLauncher />
        {themeToggle}
        {bell}
        {accountMenu}
      </div>
    )
  }

  // The rail stacks in both states: expanded they are full-width labelled rows
  // matching the nav items above (18a); collapsed the rail is 68px wide, so
  // 44px targets cannot sit side by side.
  return (
    <div className={cn('flex flex-col gap-1', collapsed && 'items-center', className)}>
      <HelpLauncher expanded={railRow} />
      <FeedbackLauncher expanded={railRow} />
      {themeToggle}
      {accountMenu}
    </div>
  )
}
